import type { PlatformDatasetSummary } from "@tdelivr/api-client/generated/data-platform/data-catalog-lineage.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { asDay, type BoardCell, type BoardRow } from "./board.ts";

/**
 * What the data catalogue and lineage board does to the one listing it reads, away from any
 * rendering.
 *
 * Built to `16 WP15 Platform Machinery.dc.html`, page `data`, against one operation:
 * DAT-03 `GET /v1/platform/catalog/datasets` —
 * [ADR 0069](../../../docs/adr/0069-a-platform-scoped-dataset-listing-carries-its-lineage-in-the-row-and-withholds-the-approver-it-holds.md),
 * which is **DAT-03's first public business operation and its first collection read of any kind**,
 * and the first listing on this platform to carry a related collection inside a row.
 *
 * ```
 * data:['Data catalogue and lineage','DAT-03',
 *   'What each dataset came from and what it feeds, in both directions. Publishing needs an approver.',
 *   [['Datasets','84','done','published'],['Awaiting approval','1','human',''],
 *    ['Quality failures','2','caution','DAT-02'],['Lineage gaps','0','done','']],
 *   ['Dataset','Upstream','Downstream'],
 *   [['finance.settlements','ledger, payouts','reports, reconciliation','done','published'],
 *    ['ops.trips_daily','trip events','SLO board','caution','quality check failed'],
 *    ['risk.features','payments, devices','fraud model v9','human','awaiting approval']],
 *   'lock','Publishing reuses the approval pattern.']
 * ```
 *
 * ## All three columns have a source, and all three of the designed badge words do not
 *
 * | drawn                                | source                                                      |
 * | ------------------------------------ | ----------------------------------------------------------- |
 * | the rows                             | `DAT-03 GET /v1/platform/catalog/datasets` (ADR 0069)       |
 * | `Dataset` — `finance.settlements`    | `datasets.displayName`                                      |
 * | `Upstream` — `ledger, payouts`       | `datasets.upstreamDatasetIds`, named through this same page  |
 * | `Downstream` — `reports, reconciliation` | `datasets.downstreamDatasetIds`, the same way            |
 * | `done · published`                   | **no such state.** `lifecycleState` has five values and `published` is none of them |
 * | `caution · quality check failed`     | **DAT-02's**, a service this page does not own and which publishes nothing public |
 * | `human · awaiting approval`          | **unmodelled.** A proposal in flight is not a row at all    |
 * | `Datasets · 84`, `Lineage gaps · 0`  | **nothing** — R-04, Q-178, and a gap is not modelled        |
 * | `Awaiting approval · 1`              | **unmodelled**, as the badge is                              |
 * | `Quality failures · 2 · DAT-02`      | **DAT-02's**, as the badge is                                |
 * | `Register a dataset`                 | **drawn and refused.** `REGISTER_REFUSAL`, ADR 0069 §10     |
 *
 * This is the inverse of the risk board one page over: there, two services published nothing and the
 * columns are empty; here every column is filled and **every designed badge word is wrong about this
 * platform**, so the badge carries DAT-03's own five words instead. ADR 0069's "What this does not
 * do" records each of the four, and three of them are not route work in any service:
 *
 *   - **`published` is not a lifecycle state.** The five are
 *     `experimental | provisional | certified | deprecated | restricted`, and they are about a
 *     dataset's maturity rather than its approval. The board draws the state the service holds.
 *   - **`Awaiting approval` and `human · awaiting approval` are unmodelled, not unpublished.** A
 *     `dataset_versions` row cannot exist without an `approved_by` — `CHECK (requested_by <>
 *     approved_by)`, enforced again in `publishDataset` and again in `validateDatasetProposal` — so a
 *     proposal in flight is not a row. A public read cannot close that: an in-flight proposal is a
 *     new aggregate rather than a projection.
 *   - **`Quality failures · 2 · DAT-02` and `caution · quality check failed` are DAT-02's.** DAT-03
 *     is `data-catalog-lineage`; DAT-02 is `data-quality`, `navigation.ts` declares this page's owner
 *     as DAT-03 alone, and DAT-02 publishes nothing a browser can call. The design labelling a tile
 *     with a service the page does not own is a finding about the design, recorded in ADR 0069.
 *   - **`Lineage gaps · 0` has nothing behind it.** DAT-03 models a cycle and a size bound and
 *     refuses both at write time, which is a different thing: a gap would be a dataset with no
 *     recorded provenance, and nothing distinguishes "no upstream yet" from "no upstream ever".
 *
 * ## The lineage cells are names, resolved by exact identifier match inside the page in hand
 *
 * `upstreamDatasetIds` and `downstreamDatasetIds` are arrays of `datasetId`, which is
 * `urn:tdelivr:dataset:<domain>:<name>`. The design's cells are short names — `ledger, payouts` —
 * and the name is a dataset's `displayName`, which **this same listing publishes for every row**.
 *
 * So `datasetNames` builds a map from `(tenantId, datasetId)` to `displayName` over the page the
 * service answered, and a lineage cell draws the name where that map has one. Three properties make
 * that an exact join rather than a guess, and all three are ADR 0069's:
 *
 *   - **The key is the primary key.** `datasetId` in a lineage array is the same value as
 *     `datasetId` on a row, byte for byte. No prefix rule, no normalisation, no last-segment
 *     truncation — the mistake `event-catalogue.ts` refuses for its topic-to-event-type match.
 *   - **The tenant is part of the key.** `lineage_edges` is tenant-scoped and `requireDatasets`
 *     refuses an edge whose endpoints are not both in the same tenant's catalogue (ADR 0069 §4), so
 *     a row's lineage never names another tenant's dataset — and the map is keyed by the pair so a
 *     cross-tenant collision cannot happen even if one did.
 *   - **A miss falls back to the published identifier, in full.** A referenced dataset may be on a
 *     later page, so the cell draws the URN rather than nothing, in Geist Mono and with the reason on
 *     it. Half an identifier addresses nothing, so it is never shortened.
 *
 * **An empty array draws the design's own `—`**, with the reason that this is DAT-03 recording no
 * lineage edge in that direction rather than a column with no source. That is exactly the
 * distinction the `Lineage gaps` tile wanted and nothing models: no upstream yet and no upstream
 * ever are the same empty array.
 *
 * **A truncated array says so.** DAT-03 carries at most twelve identifiers per direction and answers
 * `upstreamTruncated` / `downstreamTruncated` beside them, and ADR 0069 §4 says the flag "is what
 * stops a board drawing a partial lineage as complete". The twelve are drawn, the flag is on the
 * cell's `title`, and `catalogueNote` says under the grid how many rows carry more lineage than the
 * row could. The design has no vocabulary for "and more" in a cell, so none is invented in one.
 *
 * ## What DAT-03 publishes, and what it holds back although it holds it
 *
 * Published, ten: `tenantId`, `datasetId`, `displayName`, `lifecycleState`, `version`, `publishedAt`,
 * `upstreamDatasetIds`, `downstreamDatasetIds`, `upstreamTruncated`, `downstreamTruncated`.
 *
 * Withheld, each for ADR 0069 §5 and §6's reason, and the first is the one that record says to argue
 * with: **`requested_by` and `approved_by`** — two named staff subjects of every tenant on one page
 * is an identity disclosure across the tenancy boundary, and the design's approval claim is a purpose
 * sentence and a `lock` note rather than a column; **`approval_reference` and `change_reason`** — the
 * approval evidence and an operator's own words about why it changed its data model;
 * **`location_reference`** — a map of each operator's storage estate; **`source_contract_reference`
 * and `retention_policy_reference`** — governance pointers into another operator's documents;
 * **`description` and `grain`** — prose describing another operator's data model, where "one row per
 * completed trip per driver per day" is its operating model written down; **`owner_team` and
 * `tags`** — an org chart and a vocabulary across every tenant; **`classification`** — which of
 * another operator's datasets are `restricted` is a map of where its personal data is;
 * **`asset_kind`** — the grid draws no per-row glyph; **`etag`** — a concurrency token for a write
 * this read does not publish; and **from each lineage edge, `transformation_reference`,
 * `integration_evidence_reference`, `recorded_by`, `recorded_at`, `observed_at` and `edge_id`** —
 * where a transformation reference is the most specific thing in the schema about how one operator
 * derives one dataset from another.
 *
 * The design draws no column for any of them. The purpose sentence "Publishing needs an approver" is
 * drawn word for word and the approver's name is not, which is ADR 0069 §5's decision rather than an
 * absence this board discovered.
 */

/**
 * The scope DAT-03's platform dataset listing is bought with.
 *
 * A literal in this application's source, which is what grants it: `channel-scopes.mjs` derives
 * `tdelivr-platform-console`'s realm grant from the scope strings this console's own files name, so
 * naming it here is the grant and nothing else is. ADR 0032's mechanism, ADR 0049's rule that a
 * channel names only the scopes its drawn screens spend, and ADR 0069 §2 on why `data-catalog:read`
 * is not widened instead — it answers every field of a named dataset version, requester, approver,
 * location URN, grain prose and classification included.
 *
 * The literal lives in `navigation.ts`, where all eighteen of this console's cross-boundary
 * scopes are collected so that "what can this console reach" is answerable from one file.
 * Re-exported here so a reader of this module finds it beside the reasoning, and so
 * `channel-scopes.mjs` sees one literal rather than two.
 *
 * **Q-139 takes another occasion here.** Nobody has decided who may hold a scope that reads across
 * every tenant on the platform, and this one reads the name and the lineage of every dataset on it.
 */
export { DATA_CATALOGUE_SCOPE } from "./navigation.ts";

/**
 * How many lineage identifiers one row carries per direction, which is DAT-03's own cap.
 *
 * `PLATFORM_LINEAGE_CELL_LIMIT` in that service, and twelve because the cap is a grid cell's rather
 * than an API's — the internal lineage read caps at five hundred, which is right for a lineage API
 * and wrong for two cells of a table row (ADR 0069 §4). Written here so the sentence this board puts
 * on a truncated cell names the same number the service enforced.
 */
export const LINEAGE_CELL_LIMIT = 12;

/** The states DAT-03's `lifecycleState` can answer, in the order maturity reaches them. */
export type DatasetState =
  "experimental" | "provisional" | "certified" | "deprecated" | "restricted";

/**
 * The badge's class and word, from `lifecycleState` alone.
 *
 * **None of the design's three badge words is a state this platform holds**, which is this page's
 * own finding rather than one of the families the other boards carry: `published` matches none of the
 * five values, `quality check failed` is DAT-02's, and `awaiting approval` is unmodelled because a
 * row cannot exist without an approver. So all five words are DAT-03's own, and the classes are the
 * token system's nine read against what each state means.
 *
 * | `lifecycleState` | class     | why that class                                                 |
 * | ---------------- | --------- | -------------------------------------------------------------- |
 * | `experimental`   | `active`  | "Moving, not finished" — maturity under way, nothing concluded  |
 * | `provisional`    | `caution` | "An outcome with a condition attached. Readable, not alarming"  |
 * | `certified`      | `done`    | "Succeeded", and the design's own class for its published row   |
 * | `deprecated`     | `closed`  | "Ended without success or failure", whose examples are `archived` |
 * | `restricted`     | `caution` | a catalogue entry with a condition on who may read it           |
 *
 * Two readings there are decisions rather than transcriptions:
 *
 * **`certified` takes the design's class with the contract's word.** The design's first row is
 * `done · published`; `certified` is the state that row is about — a dataset signed off and relied on
 * — so the class is copied and the word is the one the service holds. Writing `published` would be
 * copy for a state that does not exist, which is worse than a word the design did not draw.
 *
 * **`restricted` shares `caution` with `provisional`, and the reason is that the class it wants is
 * not in the token system.** Seven design files draw a tenth state class, `lock`, and
 * `StateClassKey` has nine — Q-193 is that finding, and a restricted dataset is exactly what the
 * tenth would be for. `caution` is the nearest of the nine: "an outcome with a condition attached",
 * and a restricted dataset is catalogued with a condition on who may query it. The two states carry
 * different words and different `title`s, so nothing is shown by colour alone and nothing is
 * conflated — the same call `event-catalogue.ts` made for its two `failed` states.
 */
export function stateOf(summary: PlatformDatasetSummary): {
  classKey: StateClassKey;
  label: DatasetState;
  title?: string;
} {
  switch (summary.lifecycleState) {
    case "experimental":
      return {
        classKey: "active",
        label: "experimental",
        title:
          "The least mature of DAT-03's five lifecycle states. The design's own badge words — published, awaiting approval, quality check failed — match none of the five: the first is not a state, the second is unmodelled because a dataset version cannot exist without an approver, and the third is DAT-02's. ADR 0069.",
      };
    case "provisional":
      return {
        classKey: "caution",
        label: "provisional",
        title:
          "Catalogued and not yet certified. The design has no row for this state; the word is DAT-03's own.",
      };
    case "certified":
      return {
        classKey: "done",
        label: "certified",
        title:
          "Signed off. The design writes done · published here, and published is not one of DAT-03's five lifecycle states — the class is the design's and the word is the service's. ADR 0069.",
      };
    case "deprecated":
      return {
        classKey: "closed",
        label: "deprecated",
        title:
          "On its way out. The reason and the replacement are not published: this listing answers ten fields and the change reason is an operator's own words about its data model. ADR 0069 §6.",
      };
    case "restricted":
      return {
        classKey: "caution",
        label: "restricted",
        title:
          "Catalogued with a condition on who may query it. The design files draw a tenth state class for a locked record, lock, which the token system does not carry — Q-193 — so this takes the nearest of the nine.",
      };
  }
}

/** The states this listing can answer, in the order `stateOf` decides them. */
export const DATASET_STATES: readonly DatasetState[] = [
  "experimental",
  "provisional",
  "certified",
  "deprecated",
  "restricted",
];

/** The states present on the rows the board is holding, so the filter offers nothing empty. */
export function statesPresent(
  summaries: readonly PlatformDatasetSummary[],
): readonly DatasetState[] {
  const present = new Set(summaries.map((summary) => stateOf(summary).label));
  return DATASET_STATES.filter((label) => present.has(label));
}

/**
 * The map key: a tenant and a dataset, which is DAT-03's primary key for a catalogued dataset.
 *
 * The pair rather than the identifier alone, because a `datasetId` is unique only within a tenant —
 * ADR 0069 §7's reason for the cursor being the pair — and this listing spans every tenant. Joined
 * with a NUL, which neither a UUID nor a `urn:tdelivr:dataset:…` can contain, so two different pairs
 * cannot produce one key.
 */
function nameKey(tenantId: string, datasetId: string): string {
  return `${tenantId}\u0000${datasetId}`;
}

/**
 * Every dataset the answered page can put a name to, keyed by tenant and identifier.
 *
 * Built over the whole page rather than per row, so a lineage cell is one lookup rather than a scan,
 * and built from the listing's own rows rather than from a second call: the internal lineage read
 * answers one dataset and one direction, so resolving names that way would be 2N+1 calls under a
 * scope that also answers every withheld field — ADR 0069 §4's rejected alternative, arriving on the
 * client side.
 */
export function datasetNames(
  summaries: readonly PlatformDatasetSummary[],
): ReadonlyMap<string, string> {
  return new Map(
    summaries.map((summary) => [nameKey(summary.tenantId, summary.datasetId), summary.displayName]),
  );
}

/**
 * One lineage cell: the datasets this one came from, or the ones it feeds.
 *
 * @param ids the identifiers DAT-03 carried in the row, at most twelve.
 * @param truncated DAT-03's own flag saying there are more than it carried.
 * @param tenantId the row's tenant, which is half of the key every identifier resolves under.
 * @param names the page's own identifier-to-name map, from `datasetNames`.
 * @param direction `upstream` or `downstream`, for the sentence on the cell.
 */
export function lineageCell(
  ids: readonly string[],
  truncated: boolean,
  tenantId: string,
  names: ReadonlyMap<string, string>,
  direction: "upstream" | "downstream",
): BoardCell {
  if (ids.length === 0) {
    return {
      text: "",
      absent: true,
      title: `DAT-03 records no ${direction} lineage edge for this dataset. That is an empty edge list rather than a column with no source — and nothing distinguishes "no ${direction} yet" from "no ${direction} ever", which is why the design's Lineage gaps tile has nothing behind it. ADR 0069.`,
    };
  }

  const resolved = ids.map((id) => names.get(nameKey(tenantId, id)));
  const unresolved = resolved.filter((name) => name === undefined).length;
  const text = ids.map((id, index) => resolved[index] ?? id).join(", ");

  const whole =
    unresolved === 0
      ? `Named from this page's own rows by exact (tenant, dataset) match: every identifier DAT-03 carried is a dataset the catalogue answered.`
      : `${String(unresolved)} of ${String(ids.length)} are drawn as the published urn:tdelivr:dataset identifier, because the dataset they name is not on the page the catalogue answered. Written in full rather than shortened to its last segment: half an identifier addresses nothing.`;

  /*
   * `ids.length` rather than the constant, although DAT-03 sets the flag only when it carried
   * exactly twelve: the sentence says what is in the cell rather than what the cap is, so it stays
   * true if the cap moves. The cap is named beside it because that is the reason the cell is short.
   */
  const more = truncated
    ? ` DAT-03 carries at most ${String(LINEAGE_CELL_LIMIT)} per direction and this dataset has more, so this cell is the ${String(ids.length)} it carried rather than the whole lineage. ADR 0069 §4.`
    : "";

  return {
    text,
    // Mono where any value is a published identifier rather than a name, which is `board.ts`'s own
    // extension of the design's prefix rule to an identifier drawn in place of a name.
    ...(unresolved > 0 ? { mono: true } : {}),
    title: `${whole}${more}`,
  };
}

/**
 * One row of the data catalogue and lineage board.
 *
 * @param summary one catalogued dataset, as DAT-03's platform listing answered it.
 * @param names the page's identifier-to-name map, from `datasetNames`.
 */
export function datasetRow(
  summary: PlatformDatasetSummary,
  names: ReadonlyMap<string, string>,
): BoardRow {
  const state = stateOf(summary);

  return {
    /*
     * The tenant and the dataset: DAT-03's key for a catalogued dataset and this listing's cursor.
     * Prefixed with the tenant because `datasetId` is unique only within one — ADR 0069 §7 — so a
     * React key that collided would make one tenant's row replace another's on a board whose whole
     * purpose is seeing every tenant's.
     */
    id: `${summary.tenantId}:${summary.datasetId}`,
    cells: [
      /*
       * `displayName`, which is what the design's cell is: `finance.settlements`, `ops.trips_daily`,
       * `risk.features`. Not mono — the design's grid takes Geist Mono for a value matching its own
       * prefix list, and a display name matches none of it. The URN is in the detail panel, where a
       * value a cell cannot carry goes.
       */
      { text: summary.displayName },
      lineageCell(
        summary.upstreamDatasetIds,
        summary.upstreamTruncated,
        summary.tenantId,
        names,
        "upstream",
      ),
      lineageCell(
        summary.downstreamDatasetIds,
        summary.downstreamTruncated,
        summary.tenantId,
        names,
        "downstream",
      ),
    ],
    state,
    /*
     * No row on this board is tinted, and that is the design's own behaviour read honestly rather
     * than a tint withheld.
     *
     * The design tints its `human` and `failed` rows, and its one `human` row here is
     * `awaiting approval` — a state no `dataset_versions` row can be in, because a row cannot exist
     * without an approver. So this listing reaches neither class, and tinting a `caution` row with
     * `--humanRow` instead would mean "a person is waited on", which is the one thing this record
     * can never be waiting for.
     */
    detail: [
      { label: "Tenant", value: summary.tenantId, mono: true },
      /*
       * The dataset's own identifier, which the grid draws a name for: `urn:tdelivr:dataset:<domain>:
       * <name>`. This is the value the lineage arrays are made of, so the panel is where an operator
       * can read the key a cell resolved.
       */
      { label: "Dataset", value: summary.datasetId, mono: true },
      { label: "Lifecycle state", value: summary.lifecycleState },
      /*
       * Whether each direction is the whole lineage. Published as two booleans and drawn as a
       * sentence, because the flag is the only thing standing between a twelve-item cell and a
       * reader believing it is complete — ADR 0069 §4.
       */
      {
        label: "Lineage carried",
        value: lineageCarried(summary),
      },
    ],
    /*
     * The design's `History` block, with the one instant this record has.
     *
     * That block reads `Created`, `Last changed` and `Version` on every page of every one of these
     * design files, with a person's name beside the first two. **`dataset_versions` is append-only
     * by `REVOKE UPDATE, DELETE`** (ADR 0069 §7): a publication adds a row with a new `version`
     * rather than changing one, so there is no "last changed" instant to publish and the absence is
     * a property of the record rather than of the projection. Drawn as the design's own `—` and kept
     * in the block rather than dropped, so the panel's shape does not change between boards. The
     * two names have no source here either — DAT-03 holds a requester and an approver and ADR 0069
     * §5 withholds both. Q-107.
     */
    history: [
      { label: "Created", value: asDay(summary.publishedAt) },
      { label: "Last changed", value: "", absent: true },
      { label: "Version", value: `v${String(summary.version)}` },
    ],
    /** The design's own glyph for this panel, which is `FORMS.data`'s icon: `dataset`. */
    glyph: "dataset",
  };
}

/** The `Lineage carried` line: whether each direction's cell is the whole of it. */
function lineageCarried(summary: PlatformDatasetSummary): string {
  const upstream = summary.upstreamTruncated
    ? `first ${String(summary.upstreamDatasetIds.length)} of more`
    : `all ${String(summary.upstreamDatasetIds.length)}`;
  const downstream = summary.downstreamTruncated
    ? `first ${String(summary.downstreamDatasetIds.length)} of more`
    : `all ${String(summary.downstreamDatasetIds.length)}`;
  return `upstream ${upstream}, downstream ${downstream}`;
}

/**
 * What the board owes an operator about the listing behind it, in one sentence each.
 *
 * Three things, and each is said rather than implied because each invites a stronger reading than
 * the data supports:
 *
 *   - **how many tenants the rows came from**, because the page is a board of every catalogued
 *     dataset on the platform and cannot show that it is: nothing publishes how many tenants exist —
 *     `POST /v1/tenants` exists and there is no `GET` — so "84 across 5 of 7 tenants" is unsayable
 *     and "datasets from 3 tenants" is exactly true. `partner-api.ts`'s `tenantSpanNote` is the same
 *     sentence for the same reason.
 *   - **how many rows carry more lineage than the row could**, because a twelve-item cell that is the
 *     head of a longer list reads as a complete provenance, and the whole purpose of DAT-03
 *     publishing a truncation flag is that a board can say so.
 *   - **that the designed badge words and three of the four tiles are about records this platform
 *     does not hold**, which is the one set of absences a reader cannot discover by hovering a cell
 *     they did not think to hover: `published` is not a lifecycle state, an in-flight proposal is
 *     not a row, and the quality failures are DAT-02's.
 *
 * @param summaries the datasets on the board, or `undefined` before they have arrived.
 */
export function catalogueNote(
  summaries: readonly PlatformDatasetSummary[] | undefined,
): string | undefined {
  if (summaries === undefined) return undefined;
  const tenants = new Set(summaries.map((summary) => summary.tenantId)).size;
  if (tenants === 0) return undefined;

  const span = `Datasets from ${String(tenants)} tenant${tenants === 1 ? "" : "s"}.`;

  const cut = summaries.filter(
    (summary) => summary.upstreamTruncated || summary.downstreamTruncated,
  ).length;
  const lineage =
    cut === 0
      ? `Every row's lineage is the whole of it: no dataset here has more than ${String(LINEAGE_CELL_LIMIT)} edges in either direction.`
      : `${String(cut)} of ${String(summaries.length)} carry more lineage than a row holds, so those cells are the first ${String(LINEAGE_CELL_LIMIT)} by identifier rather than the whole provenance.`;

  const states = "The badge draws DAT-03's own five lifecycle states.";

  return `${span} ${lineage} ${states}`;
}

/**
 * Why the designed `Register a dataset` form is drawn and refused.
 *
 * Its fields are `Name`, `Owner team`, `Upstream sources` and `Contains personal data`, and its
 * primary is `Submit for approval`. Four reasons, and any one is enough — ADR 0069 §10 measured each:
 *
 *   - **The publish command is `/internal/v1` only**, with a `tenantId` and a `datasetId` on the
 *     path, and the router answers 404 on its platform-shaped path deliberately.
 *   - **It needs two different people.** `CHECK (requested_by <> approved_by)` in the table,
 *     `publishDataset` refusing an actor who is the requester, and `validateDatasetProposal`
 *     refusing the equality again — three layers, so one administrator pressing this could not
 *     satisfy it even if the route were public.
 *   - **The body has eleven required fields and the form collects four**, of which three do not
 *     match: `Owner team` is a select of four against free text, `Contains personal data` is a toggle
 *     against a four-value `classification`, and `Upstream sources` is a chip set that maps to a
 *     **second command** — `POST …/catalog/lineage-edges` — rather than to a field of the publish.
 *   - **`Submit for approval` describes the approval pattern the page's `lock` note names**, and the
 *     approval evidence reference it would carry is one of the fields ADR 0069 §6 withholds.
 *
 * Drawn and disabled rather than removed, because `CLAUDE.md` §1 says the add or edit form is not to
 * be removed, and rather than wired to something that would answer 404.
 */
export const REGISTER_REFUSAL =
  "DAT-03 publishes the dataset publication on /internal/v1 only, which the gateway does not expose, and it takes a tenantId and a datasetId this board has no value for. It needs two different people — requested_by <> approved_by, enforced at three layers — so one administrator could not satisfy it even if the route were public. Its body has eleven required fields against this form's four: Owner team is a select of four against free text, Contains personal data is a toggle against a four-value classification, and Upstream sources maps to a second command, POST …/catalog/lineage-edges. ADR 0069 §10.";
